// 009 FR-040..FR-043: the payment adapter. Its one job: open the provider's payment screen with the
// server's payment session and return what the provider said. The server's checkout status is the
// only outcome (FR-041). The fake adapter is for local/staging (the server refuses `fake` in
// production); Razorpay runs Checkout.js on the web; the Android SDK isn't in this build yet.
import { Platform } from "react-native";

import type { Api } from "@/api/types";

export type GatewayResult =
  | { type: "success"; result: Api["PaymentResult"] | null }
  | { type: "failure" }
  | { type: "cancelled" }
  | { type: "unavailable" };

export type PaymentAdapter = {
  open(session: Api["PaymentSession"]): Promise<GatewayResult>;
};

/** The fake sheet's answer; set by FakePaymentSheet while it is open. */
type FakeRequest = (session: Api["PaymentSession"]) => Promise<GatewayResult>;
let fakeRequest: FakeRequest | null = null;
export function registerFakeSheet(request: FakeRequest | null): void {
  fakeRequest = request;
}

const fakeAdapter: PaymentAdapter = {
  open: (session) =>
    fakeRequest
      ? fakeRequest(session)
      : Promise.resolve({ type: "unavailable" }),
};

type RazorpayHandler = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};
type RazorpayCtor = new (options: Record<string, unknown>) => {
  open(): void;
  on(event: string, cb: () => void): void;
};

function loadRazorpay(): Promise<RazorpayCtor | null> {
  const w = globalThis as unknown as {
    Razorpay?: RazorpayCtor;
    document?: Document;
  };
  if (w.Razorpay) return Promise.resolve(w.Razorpay);
  if (!w.document) return Promise.resolve(null);
  return new Promise((resolve) => {
    const script = w.document!.createElement("script");
    // The gateway's own script, not an API host (Razorpay Checkout on the web).
    // eslint-disable-next-line no-restricted-syntax
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(w.Razorpay ?? null);
    script.onerror = () => resolve(null);
    w.document!.head.appendChild(script);
  });
}

const razorpayWeb: PaymentAdapter = {
  async open(session) {
    const Razorpay = await loadRazorpay();
    if (!Razorpay) return { type: "unavailable" };
    return new Promise<GatewayResult>((resolve) => {
      const checkout = new Razorpay({
        ...session.clientPayload,
        order_id: session.providerOrderId,
        amount: session.amountInPaise,
        currency: session.currency,
        handler: (r: RazorpayHandler) =>
          resolve({
            type: "success",
            result: {
              providerOrderId: r.razorpay_order_id,
              providerPaymentId: r.razorpay_payment_id,
              signature: r.razorpay_signature,
            },
          }),
        modal: { ondismiss: () => resolve({ type: "cancelled" }) },
      });
      checkout.on("payment.failed", () => resolve({ type: "failure" }));
      checkout.open();
    });
  },
};

/** The adapter for the server's provider name (FR-040). */
export function adapterFor(provider: string): PaymentAdapter {
  if (provider === "fake") return fakeAdapter;
  if (provider === "razorpay" && Platform.OS === "web") return razorpayWeb;
  return { open: async () => ({ type: "unavailable" }) };
}
